"use client";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/admin/_common/ui/dialog";
import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { Checkbox } from "@/app/admin/_common/ui/checkbox";
import CuoButton from "@/app/admin/_common/CuoButton";
import { stripeDashboardPaymentUrl } from "@/app/_utils/stripeDashboard";
import { ArrowLeftRight } from "lucide-react";
import { toast } from "@/app/_utils/toast";
import { getCsrfToken } from "@/app/_utils/csrf";

export default function RefundDialog({
  open,
  onClose,
  order,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  order: any;
  onSuccess?: () => void;
}) {
  const [refundAmount, setRefundAmount] = useState(1);
  const [restock, setRestock] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!order) return null;
  const { comped } = order;

  async function handleRefund() {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}show/order/`, {
        method: "PUT",
        credentials: "include",
        body: JSON.stringify({ action: "refund", orderId: order.id, refundAmount, restock }),
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
      });
      if (res.status === 200) {
        toast.success(comped ? "Guest uncomped" : "Guest refunded");
        onClose();
        if (onSuccess) onSuccess();
        else window.location.reload();
      } else {
        toast.error("An error has occurred");
      }
    } catch (e) {
      toast.error("An error has occurred");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !loading && onClose()}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <ArrowLeftRight className="size-4 flex-none text-muted-foreground" />
              {comped ? "Uncomp guest" : "Refund guest"}
            </span>
          </DialogTitle>
          <DialogDescription>
            {comped ? (
              "This cannot be undone."
            ) : (
              <>
                This cannot be undone, make sure you are refunding the correct order{" "}
                <a
                  href={stripeDashboardPaymentUrl(order.stripe_payment_intent)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                >
                  #{order.order_id}
                </a>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="refund-amount">No. of tickets to {comped ? "release" : "refund"}</Label>
            <div className="flex items-center gap-2">
              <Input
                id="refund-amount"
                type="number"
                min={1}
                max={order.quantity}
                value={refundAmount}
                onChange={(e) => setRefundAmount(Number(e.target.value))}
                className="w-24"
              />
              <span className="text-[12.5px] text-muted-foreground">/ {order.quantity}</span>
            </div>
          </div>
          <label className="flex items-center gap-2 text-[13px] text-foreground">
            <Checkbox checked={restock} onCheckedChange={(c) => setRestock(Boolean(c))} />
            Restock {refundAmount} ticket(s)
          </label>
        </div>

        <DialogFooter>
          <CuoButton variant="outline" label="Cancel" onClick={onClose} disabled={loading} />
          <CuoButton
            variant="destructive"
            disableOnAsync={loading}
            disabled={refundAmount > order.quantity}
            label={comped ? "Uncomp" : "Refund"}
            onClick={handleRefund}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
